import { Injectable, Inject } from '@angular/core';

import { Observable, Subject, Subscription, BehaviorSubject, ReplaySubject } from "rxjs";

import { AppService } from '../app.service';
import { UserService } from '../user/user.service';
import { OrganizationService } from '../organization/organization.service';
import { LocationService } from '../locations/location.service';
import { LaravelApiService } from '../laravel.service';
import { DeepstreamService } from '../deepstream/deepstream.service';
import { NotificationService } from '../shared/notification/notification.service';
import { LoadingService } from '../shared/loading/loading.service';

import { Device } from './device.model';
import { Controller, IP, Vendor } from './controller.model';

const RSSI_STRENGTH = [9, 14, 19, 99];
const RSSI_VALID_CONNECTION_TYPES = {'eth0': 'Cable', 'wlan0': 'Wi-Fi'};

@Injectable({
  providedIn: 'root'
})
export class DeviceService extends AppService {

  public dsInstance: any;

  private _devices: Device[] = [];
  public devicesSubject: BehaviorSubject<Device[]>;
  public devicesOnline: Device[] = [];
  public devicesOnlineCount: number;

  public deviceCellularData: any[] = [];
  public deviceCellularCount: number = 0;

  public deepstreamStatus: boolean = true;

  constructor(
    private user: UserService,
    @Inject(LaravelApiService) private laravel: LaravelApiService,
    private ds: DeepstreamService,
    private notificationService: NotificationService,
    private loadingService: LoadingService,
    private organizationService: OrganizationService,
    private locationService: LocationService
	) {
    super();

    this.dsInstance = this.user.dsInstance;

    let tmpDeviceArray: Device[] = [];
    this.devicesSubject = new BehaviorSubject(tmpDeviceArray);

    this.laravel.getClientConfig().subscribe((configData) => {
      if (!configData) return;

      this.dsInstance = this.ds.dsInstance;

      this.loadingService.loading = true;

      this.setupDevices()

      this.user.tempOrgIDSubject.subscribe((orgID) => {
        if(orgID) {
          this.loadingService.loading = true;
          this.setupDevices();
        }
      })
    });
  }

  get devices(): Device[] {
    return this._devices;
  }

  updateDevice(device) {
    if (!device) return;

    let deviceRef = this._find('devices', device.id);
    if (!deviceRef) {
      deviceRef = new Device(device)
    } else {
      deviceRef = Object.assign(deviceRef, device);
    }
    deviceRef.connections = deviceRef.connections.map((connection) => {
      connection.ips = connection.ips.map((ip) => new IP(ip));
      connection.vendor = new Vendor(connection.vendor);
      return new Controller(connection);
    });
    this._update('devices', device);
    this.update();
  }

  updateDevices(devices: Device[]) {
    devices.forEach(device => {
      let deviceRef = this._find('devices', device.id);
      deviceRef = Object.assign(deviceRef, device);
    });
    this.update();
  }

  //TODO: grab individual device from the DB and update in place
  device(id: number): Device {
    let deviceSub = this.laravel.fetch("user/devices", id);
    deviceSub.subscribe( ( device) => {
      if (!device) return;

      this.updateDevice(device);
    })

    return this._find('devices', id);
  }

  async deviceAsync(id: number): Promise<Device> {
    return new Promise((resolve, reject) => {
      let deviceSub = this.laravel.fetch("user/devices", id);
      deviceSub.subscribe( ( device) => {
        if (device) {
          this.updateDevice(device);
          resolve(this._find('devices', id));
        }
      })
    })
  }

  getDeviceByCode(code: string): Device | undefined {
    return this._devices.find((device) => device.code == code);
  }

  addDevice(device) {
    let newDevice = new Device(device);
    this._devices.push(newDevice);
    this.update();
  }

  createDevice(device: Device){
    let request = this.laravel.create("user/devices", device);
    request.subscribe((device) => {
      let newDevice = new Device(device);
      this._devices.push(newDevice);
      this.update();
      this.notificationService.success(`Device ${device.name} created!`, 3000)
    });
    return request;
  }

  saveDevice(device: Device) {
    const updateObservable = this.laravel.update("user/devices", device.id, device);
    updateObservable.subscribe((result) => {
      if (result) {
        this.updateDevice(device);
        this.notificationService.success(`Device ${device.name} saved!`, 3000)
      }
    });
    return updateObservable;
  }

  deleteDevice(id: number) {
    const deleteObservable = this.laravel.delete("user/devices/remote/connection_devices", id);
    deleteObservable.subscribe((result) => {
      if (result) {
        let device = this.device(id);
        let name = device.name;
        this._delete('devices', id);
        this.update();
        this.notificationService.success(`Device ${name} deleted!`, 3000)
      }
    });
    return deleteObservable;
  }

  deleteDevices(ids: number[]) {
    const deleteObservable = this.laravel.deleteSeveral("user/devices/remote/connection_devices", ids);
    deleteObservable.subscribe((deletedIds: any[]) => {
      deletedIds.forEach((id) => this._delete('devices', id));
      this.update();
      this.notificationService.success(`${ids.length} devices deleted!`, 3000)
    });
    return deleteObservable;
  }

  update() {
    this.devicesSubject.next(this._devices);
  }

  setupDevices() {
    let deviceSub = this.laravel.list('user/devices/remote/connections');

    deviceSub.subscribe( (data) => {
      if (!data) return;

      this._devices = data.map((device) => {
        device = new Device(device);
        device.org =  this.organizationService.organization(device.org_id);
        return device;
      });
      this.update();

      this.subscribeToDevicePresence();
      if (this.deviceCellularData.length === 0) {
        this.getDeviceCellularList()
          .subscribe((data) => {
            if (data) {
              if (!Array.isArray(data)) data = [data];
              this.deviceCellularData = data;
              this.populateCellData();
            }
          })
      } else {
        this.populateCellData();
      }

      this.checkConnections(this.user.userInfo.id);

      this.organizationService.subOrgsSubject.subscribe((orgs) => {
        this._devices.forEach((device) => {
          device.org =  this.organizationService.organization(device.org_id);
        })
        this.update();
      })

      this.loadingService.loading = false;
    });
  }

  populateCellData() {
    console.log(this.deviceCellularData);
    if (this.deviceCellularData) {
      this.deviceCellularData.forEach((link) => {
        let device = this.getDeviceByCode(link.devicename);
        if (device) {
          this.deviceCellularCount++;
          if (device.hologram_device_id) {
            /*
            this.getDeviceCellular(device.hologram_device_id)
              .subscribe((data) => {
                if (device && data.links && data.links.cellular.length > 0) {
                  let link = data.links.cellular[0];
                  device.cell_state = this.convertDeviceState(link.state);
                  device.sim = link.sim;

                  device.cell_usage = this.formatCellUsageData(link.cur_billing_data_used);
                  device.cell_limit = this.formatCellUsageData(link.plan.data);
                }
              });
             */
          }
        }
      })
      this.update();
    }
  }

  formatCellUsageData(val) {
    let retVal: any = { value: null, unit: null };
    retVal.value = val / 1000;
    retVal.unit = 'KB';
    if (retVal.value > 1000) {
      retVal.value /= 1000;
      retVal.unit = 'MB';
    }
    retVal.value = Math.round(retVal.value * 10) / 10;
    return retVal
  }

  convertDeviceState(state) {
    if (state.toLowerCase().includes('pause')){
      state = 'paused';
    }

    if (state.toLowerCase().includes('live')){
      state = 'live';
    }
    return state;
  }

  countOnlineDevices() {
    let count = 0;
    for (let device in this.devicesOnline) {
      if (this.devicesOnline.hasOwnProperty(device) && this.devicesOnline[device]) count++;
    }
    this.devicesOnlineCount = count;
  }

  subscribeToDevicePresence() {
    console.log('sub to device presence')
    this.dsInstance.presence.subscribe((code, isLoggedIn) => {
      let device = this.getDeviceByCode(code);
      if (device) {
        device.online = isLoggedIn;
        if (device.online) {
          this.devicesOnline[device.code] = true;
          this.getRemoteDeviceRSSI(device)
        } else {
          this.devicesOnline[device.code] = false;
          this.stopRemoteDeviceRSSI(device)
        }
        this.countOnlineDevices();
      }
    })

    this.dsInstance.presence.getAll((error, devices) => {
      if (devices) {
        devices.forEach((code) => {
          let device = this.getDeviceByCode(code);
          if (device) {
            device.online = true;
            this.devicesOnline[device.code] = true;
						this.getRemoteDeviceRSSI(device);
          } else {
            //this.devicesOnline[device.code] = false;
          }
        });
        this.countOnlineDevices();
        this.update();
      }
    });
  }

  async getDeviceRecord(ip, deviceId) {
    let record = this.dsInstance.record.getRecord(`device/${ip}/eclypse/devices/${deviceId}`);
    await record.whenReady();
    this.setDeviceRecord(deviceId, record.get());
    record.subscribe( data => {
      this.setDeviceRecord(deviceId, record.get());
    });
  }

  setDeviceRecord(id, data) {
    let device = this.device(id);
    if (device) {
      device = Object.assign(device, data);
      this.updateDevice(device);
    }
  }

  activateDevice(deviceId: number) {
    let device = this.device(deviceId);
    this.setDevicesConnectionStatus(device.id, 'connecting');
    let reqData = {
      user_route: device.vpn_ip,
      site_id: device.id
    }
    this.connectTunnel(reqData).subscribe((res) => {
      if (res.vpn === false) {
        this.setDevicesConnectionStatus(null);
        this.notificationService.error(`VPN not connected!`, 3000)
      } else {
        this.setDevicesConnectionStatus(device.id);
        this.notificationService.success(`Connected to ${device.name} via VPN.`, 3000)
      }
    });
  }

  deactivateDevice(deviceId: number | null) {
    if (deviceId) this.setDevicesConnectionStatus(deviceId, 'connecting');

    this.connectTunnel({user_route: 'null'}).subscribe((res) => {
      this.setDevicesConnectionStatus(null);
    });
  }

  // Set the connection status of device, connection,
  // and IP at the same time while disconnecting others.
  setDevicesConnectionStatus(deviceId, status?: string) {
    let statusString = status ? status : 'connected';
    this._devices.forEach((device) => {
      device.connection_status = device.id === deviceId ? statusString : 'disconnected';
    })
    this.update();
  }

  reboot(device: Device) {
    this.dsInstance.rpc.make('reboot-remote', { serial: device.code }, (err, res) => {
      this.notificationService.success(`${device.name} rebooting.`, 3000)
    })
  }

  /*
   * Controller / IP
   */

  controller(device_id: number, controller_id: number): Controller {
    let device = this._find('devices', device_id);
    return device.connections.find((controller) => controller.id == controller_id);
  }

  rebootController(ip: string){
    return this.laravel.fetch('user/devices/remote/restart', ip);
  }

  updateDeviceRelay(device_id: number, ip_id) {
    return this.laravel.post(`user/devices/${device_id}/relay`, { ip_id: ip_id });
  }

  // IP

  addIP(controller: Controller) {
    return this.laravel.post(`user/devices/${controller.device_id}/controllers`, controller);
  }

  saveIP(controller: Controller) {
    return this.laravel.post(`user/devices/${controller.device_id}/controllers/${controller.id}`, controller);
  }

  deleteIP(ip_id: number, device_id: number) {
    return this.laravel.delete(`user/devices/${device_id}/ips`, ip_id);
  }

  connectTunnel(data) {
    return this.laravel.post('user/devices/remote', data);
  }

  checkConnections(user_id: number) {
    this.laravel.list(`user/devices/remote/${user_id}`)
      .subscribe((res) => {
        if (res) {
          if (res.vpn && res.connections !== "") {
            this.setConnectionStatus(res.connections.split(","));
          }
        }
      });
  }

  setConnectionStatus(ports: string[]) {
    this.devices.forEach((device) => {
      device.connection_status = ports.includes(device.vpn_port) ? 'connected' : 'disconnected';
    })
    this.update();
  }

  public getGuacToken(deviceId){
    return this.laravel.fetch(`user/devices/remote/guac_token`, deviceId)
  }

  public provisionDevices(){
    return this.laravel.post('user/devices/remote/provision', {});
  }

  public getNextId(){
    return this.laravel.fetch(`user/devices/remote`, 'next_id')
  }

  // Cellular

  public getDeviceCellular(deviceId){
    return this.laravel.fetch('user/devices/cellular', deviceId);
  }

  public getDeviceCellularList(deviceIds?: any){
    return this.laravel.list('user/devices/cellular/list');
  }

  // Bulk Actions

  bulkActivate(devices: Device[]) {
    let ids: Array<number> = devices.map((device: Device) => { return device.id });
    let request = this.laravel.put('user/devices/disable', { ids: ids, disable: false });
    request.subscribe((response) => {
      ids.forEach((id) => this._update('devices', {id: id, disabled_for_ordinary_users: false }));
      this.update();
      this.notificationService.success(`${devices.length}  devices updated!`, 3000)
    })
    return request;
  }

  bulkDeactivate(devices: Device[]) {
    let ids: Array<number> = devices.map((device: Device) => { return device.id });
    let request = this.laravel.put('user/devices/disable', { ids: ids, disable: true });
    request.subscribe((response) => {
      ids.forEach((id) => this._update('devices', {id: id, disabled_for_ordinary_users: true }));
      this.update();
      this.notificationService.success(`${devices.length}  devices updated!`, 3000)
    })
    return request;
  }

  pauseCellular(devices: Device[]) {
    let ids: Array<string> = devices.map((device: Device) => { return device.hologram_device_id });
    let request = this.laravel.post('user/devices/cellular/state', { ids: ids, state: 'pause' });
    request.subscribe((response) => {
      console.log(response)
      ids.forEach((id) => {
        if (id) this._update('devices', {id: id, cell_state: 'paused'})
      });
      this.update();
      this.notificationService.success(`${devices.length}  devices updated!`, 3000)
    })
    return request;
  }

  unpauseCellular(devices: Device[]) {
    let ids: Array<string> = devices.map((device: Device) => { return device.hologram_device_id });
    let request = this.laravel.post('user/devices/cellular/state', { ids: ids, state: 'live' });
    request.subscribe((response) => {
      ids.forEach((id) => {
        if (id) this._update('devices', {id: id, cell_state: 'live'})
      });
      this.update();
      this.notificationService.success(`${devices.length}  devices updated!`, 3000)
    })
    return request;
  }

  bulkEdit(devices: Device[], properties: any) {
    let ids: Array<number> = devices.map((device: Device) => { return device.id });
    let request = this.laravel.post('user/devices/bulk_edit', { ids: ids, properties: properties })
    request.subscribe((response) => {
      if (properties.org_id)
      ids.forEach((id) => {
        let updateProps = {id: id, ...properties};
        if (updateProps.org_id) updateProps.org = this.organizationService.organization(properties.org_id);
        if (updateProps.location_id) updateProps.location = this.locationService.location(properties.location_id);
        this._update('devices', updateProps)
      });
      this.update();
      this.notificationService.success(`${devices.length}  devices updated!`, 3000)
    })
    return request;
  }

  bulkDelete(devices: Device[]) {
    let ids: Array<number> = devices.map((device: Device) => { return device.id });
    let request = this.laravel.post('user/delete_devices', { ids: ids });
    request.subscribe((response) => {
      ids.forEach((id) => this._delete('devices', id));
      this.update();
      this.notificationService.success(`${devices.length}  devices deleted!`, 3000)
    })
    return request;
  }

  getRemoteDeviceRSSI(device) {
    //if (this.user.userInfo.group.id === 1) return;

    this.dsInstance.event.subscribe(`device/${device.code}/rssi`, (data) => {
      if (isNaN(parseInt(data))) {
        if (RSSI_VALID_CONNECTION_TYPES[data] !== undefined) {
          device.connection_type = RSSI_VALID_CONNECTION_TYPES[data];
        } else {
          device.connection_type = 'Online';
        }
        device.rssi_value = null;
        this.stopRemoteDeviceRSSI(device);
      } else {
        device.rssi_value = this.translateRSSIData(parseInt(data, 10));
        device.connection_type = 'RSSI';
      }

		  this.update();
    })
  }

  stopRemoteDeviceRSSI(device) {
		this.dsInstance.event.unsubscribe(`device/${device.code}/rssi`);
  }

  translateRSSIData(rssi) {
    let value = RSSI_STRENGTH.reduce((val, step, i) => {
      if (rssi <= step && val === 0) val = i;
      return val;
    }, 0);
    return value;
  }
}
